import { useState, useEffect } from 'react'
import { supabase, STORAGE_BUCKETS } from '../config/supabase'
import { useAuth } from '../context/AuthContext'

export const useBookCovers = () => {
  const [covers, setCovers] = useState([])
  const [loading, setLoading] = useState(true)
  const { user } = useAuth()

  const fetchCovers = async () => {
    if (!user) {
      setLoading(false)
      return
    }

    try {
      console.log('Fetching covers for user:', user.id)
      const { data, error } = await supabase
        .from('book_covers_bc2024')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching covers:', error)
        setCovers([])
      } else {
        console.log('Fetched covers:', data)
        setCovers(data || [])
      }
    } catch (error) {
      console.error('Error fetching covers:', error)
      setCovers([])
    } finally {
      setLoading(false)
    }
  }

  const ensureBucketExists = async () => {
    try {
      console.log('Checking if bucket exists...')
      
      // Try to list files in the bucket first
      const { data: files, error: listError } = await supabase.storage
        .from('book-covers')
        .list('', { limit: 1 })

      if (listError) {
        console.log('Bucket does not exist, creating...', listError.message)
        
        // Create the bucket
        const { data: bucketData, error: createError } = await supabase.storage
          .createBucket('book-covers', {
            public: true,
            allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
            fileSizeLimit: 10485760
          })

        if (createError) {
          console.error('Failed to create bucket:', createError)
          throw new Error(`Failed to create storage bucket: ${createError.message}`)
        }

        console.log('Bucket created successfully:', bucketData)
        return true
      } else {
        console.log('Bucket already exists')
        return true
      }
    } catch (error) {
      console.error('Error ensuring bucket exists:', error)
      throw error
    }
  }

  const uploadCover = async (file, metadata = {}) => {
    if (!user || !file) {
      console.error('Missing user or file:', { user: !!user, file: !!file })
      throw new Error('Missing user or file')
    }

    console.log('Starting upload process...')
    console.log('File:', file.name, file.size, file.type)
    console.log('Metadata:', metadata)
    console.log('User:', user.id)

    try {
      // Ensure bucket exists first
      await ensureBucketExists()

      const fileExt = file.name.split('.').pop()
      const fileName = `${user.id}/${Date.now()}.${fileExt}`

      console.log('Uploading file to:', fileName)

      // Upload file to storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('book-covers')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) {
        console.error('Storage upload error:', uploadError)
        throw new Error(`Upload failed: ${uploadError.message}`)
      }

      console.log('File uploaded successfully:', uploadData)

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('book-covers')
        .getPublicUrl(fileName)

      console.log('Public URL:', publicUrl)

      // Save to database
      const dbPayload = {
        user_id: user.id,
        title: metadata.title || 'Untitled',
        author: metadata.author || 'Unknown Author',
        genre: metadata.genre || '',
        publish_date: metadata.publishDate || null,
        isbn: metadata.isbn || '',
        description: metadata.description || '',
        series: metadata.series || '',
        series_number: metadata.seriesNumber ? parseInt(metadata.seriesNumber) : null,
        file_path: fileName,
        file_url: publicUrl,
        file_size: file.size,
        file_type: file.type
      }

      console.log('Saving to database:', dbPayload)

      const { data, error } = await supabase
        .from('book_covers_bc2024')
        .insert(dbPayload)
        .select()
        .single()

      if (error) {
        console.error('Database insert error:', error)
        throw new Error(`Database error: ${error.message}`)
      }

      console.log('Book cover saved successfully:', data)
      await fetchCovers()
      return data
    } catch (error) {
      console.error('Error uploading cover:', error)
      throw error
    }
  }

  const deleteCover = async (coverId, filePath) => {
    if (!user) return false

    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('book-covers')
        .remove([filePath])

      if (storageError) {
        console.error('Storage delete error:', storageError)
      }

      // Delete from database
      const { error } = await supabase
        .from('book_covers_bc2024')
        .delete()
        .eq('id', coverId)
        .eq('user_id', user.id)

      if (error) throw error

      await fetchCovers()
      return true
    } catch (error) {
      console.error('Error deleting cover:', error)
      return false
    }
  }

  useEffect(() => {
    fetchCovers()
  }, [user])

  return {
    covers,
    loading,
    uploadCover,
    deleteCover,
    refetch: fetchCovers
  }
}